import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';

type Variant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'accent'
  | 'danger'
  | 'solid-dark';
type Size = 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-primary text-[color:var(--color-primary-ink)] hover:bg-secondary',
  secondary:
    'bg-transparent border border-[color:var(--color-border-strong)] text-on-surface hover:border-primary hover:text-primary',
  ghost:
    'bg-surface-2 text-on-surface hover:bg-surface-3',
  accent:
    'bg-accent text-[color:var(--color-accent-ink)] hover:brightness-105',
  danger:
    'bg-transparent border border-error text-error hover:bg-[color:var(--color-error-dim)]',
  'solid-dark':
    'bg-surface-3 text-on-surface hover:bg-surface-hi',
};

const SIZE: Record<Size, string> = {
  sm: 'h-9 px-[14px] text-[13px]',
  md: 'h-12 px-[18px] text-[14px]',
  lg: 'h-[54px] px-[22px] text-[15px]',
};

/** Shared class recipe — shared between Button and LinkButton. */
export function buttonClasses(
  variant: Variant = 'primary',
  size: Size = 'md',
  full = false,
  extra = '',
): string {
  return [
    'inline-flex items-center justify-center gap-2 rounded-full font-display font-semibold tracking-[0.01em] transition',
    'disabled:cursor-not-allowed disabled:opacity-60',
    VARIANT[variant],
    SIZE[size],
    full ? 'w-full' : '',
    extra,
  ].join(' ');
}

interface ButtonBaseProps {
  variant?: Variant;
  size?: Size;
  full?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
}

type ButtonProps = ButtonBaseProps & ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      full,
      leading,
      trailing,
      className = '',
      children,
      type = 'button',
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={buttonClasses(variant, size, full, className)}
        {...rest}
      >
        {leading}
        <span>{children}</span>
        {trailing}
      </button>
    );
  },
);

type LinkButtonProps = ButtonBaseProps & LinkProps;

export function LinkButton({
  variant = 'primary',
  size = 'md',
  full,
  leading,
  trailing,
  className = '',
  children,
  ...rest
}: LinkButtonProps) {
  return (
    <Link className={buttonClasses(variant, size, full, className)} {...rest}>
      {leading}
      <span>{children}</span>
      {trailing}
    </Link>
  );
}
